<template>
  <div class="attr-picker">
    <div v-if="showTabs" class="picker-tabs">
      <div
        class="picker-tab"
        :class="{ active: activeTab === 'item' }"
        @click="activeTab = 'item'"
      >
        Item
      </div>
      <div
        class="picker-tab"
        :class="{ active: activeTab === 'record' }"
        @click="activeTab = 'record'"
      >
        Все атрибуты
      </div>
    </div>

    <div class="picker-header">
      <span class="picker-title">
        {{ headerTitle }} ({{ currentAttributes.length }})
      </span>
      <input
        v-model="searchQuery"
        type="text"
        class="picker-search"
        placeholder="Поиск..."
      />
    </div>

    <div class="picker-list">
      <div
        v-for="attr in currentAttributes"
        :key="attr.path"
        class="attr-item-wrapper"
      >
        <div class="attr-item" @click="handleItemClick(attr)">
          <div
            v-if="isExpandable(attr.value)"
            class="expand-icon"
            @click.stop="toggleExpand(attr.path)"
          >
            {{ expandedKeys.includes(attr.path) ? "▼" : "▶" }}
          </div>
          <div v-else class="expand-placeholder"></div>

          <div class="attr-main">
            <span class="attr-label">{{ attr.label }}</span>
            <span class="attr-path">{{ attr.path }}</span>
          </div>

          <div class="attr-preview">
            <span
              class="preview-val"
              :class="{ 'val-empty': isEmpty(attr.value) }"
            >
              {{ formatValuePreview(attr.value) }}
            </span>
          </div>
        </div>

        <div
          v-if="
            isExpandable(attr.value) &&
            expandedKeys.includes(attr.path)
          "
          class="nested-tree-container"
        >
          <AttributeJsonTree
            :data="attr.value"
            :parent-path="attr.path"
            :is-parent-array="Array.isArray(attr.value)"
            @select="handleJsonSelect"
          />
        </div>
      </div>

      <div v-if="currentAttributes.length === 0" class="no-results">
        {{ isLoadingItemAttrs ? "Загрузка..." : "Ничего не найдено" }}
      </div>
    </div>
  </div>
</template>

<script>
import { formatDateAndTime } from "@/utils/dateUtils";
import AttributeJsonTree from "./AttributeJsonTree.vue";

export default {
  name: "TemplateAttributePicker",
  components: { AttributeJsonTree },
  props: {
    data: { type: Object, default: () => null },
    itemData: { type: Object, default: () => null },
    blockType: { type: String, default: "" },
    isLoadingItemAttrs: { type: Boolean, default: false },
  },
  emits: ["insert"],
  data() {
    return {
      searchQuery: "",
      expandedKeys: [],
      activeTab: "item",
    };
  },
  computed: {
    showTabs() {
      return ["items_table", "sectioned_items_table"].includes(
        this.blockType
      );
    },
    headerTitle() {
      if (!this.showTabs) return "Доступные поля";
      return this.activeTab === "record"
        ? "Доступные поля (Record)"
        : "Доступные поля (Item)";
    },
    recordAttributesList() {
      return this.data && this.data.parsed_attrs
        ? this.data.parsed_attrs
        : [];
    },
    itemAttributesList() {
      return this.itemData && this.itemData.parsed_attrs
        ? this.itemData.parsed_attrs
        : [];
    },
    currentRawList() {
      if (!this.showTabs) return this.recordAttributesList;
      return this.activeTab === "record"
        ? this.recordAttributesList
        : this.itemAttributesList;
    },
    currentAttributes() {
      const q = this.searchQuery.toLowerCase();

      return this.currentRawList.filter((attr) => {
        if (
          typeof attr.value === "object" &&
          attr.value !== null &&
          !Array.isArray(attr.value)
        ) {
          return false;
        }

        return (
          (attr.label && attr.label.toLowerCase().includes(q)) ||
          (attr.path && attr.path.toLowerCase().includes(q))
        );
      });
    },
    currentPrefix() {
      if (!this.showTabs) return "record";
      return this.activeTab === "record" ? "record" : "item";
    },
  },
  methods: {
    handleItemClick(attr) {
      if (this.isExpandable(attr.value)) {
        this.toggleExpand(attr.path);
      } else {
        this.selectAttribute(attr.path, this.currentPrefix);
      }
    },

    handleJsonSelect(fullPath) {
      this.selectAttribute(fullPath, this.currentPrefix);
    },

    toggleExpand(path) {
      if (this.expandedKeys.includes(path)) {
        this.expandedKeys = this.expandedKeys.filter(
          (k) => k !== path
        );
      } else {
        this.expandedKeys.push(path);
      }
    },

    selectAttribute(path, prefix) {
      this.$emit("insert", `{{ ${prefix}.${path} }}`);
    },

    isExpandable(val) {
      return val !== null && typeof val === "object";
    },

    isEmpty(val) {
      return val === null || val === undefined || val === "";
    },

    formatValuePreview(val) {
      if (val === null || val === undefined) return "нет данных";

      if (val === "") return "пустая строка";

      if (typeof val === "boolean") return val ? "Да" : "Нет";

      if (Array.isArray(val)) {
        if (val.length === 0) return "";
        return "";
      }

      if (typeof val === "object") return "";

      if (this.isDate(val)) {
        return this.formatDateAndTime(val);
      }

      return String(val);
    },

    isDate(val) {
      if (typeof val !== "string") return false;
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

      return isoDateRegex.test(val);
    },

    formatDateAndTime,
  },
};
</script>

<style scoped>
.attr-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.picker-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
}

.picker-tab {
  flex: 1;
  padding: 10px 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.picker-tab:hover {
  background: #f3f4f6;
  color: #374151;
}

.picker-tab.active {
  color: #3b82f6;
  background: #fff;
  border-bottom-color: #3b82f6;
}

.picker-header {
  padding: 12px 16px;
  background: #f0f0f0;
  border-bottom: 1px solid #e5e7eb;
}

.picker-title {
  display: block;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.picker-search {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
}

.picker-list {
  flex: 1;
  overflow-y: auto;
  padding: 5px;
}

.attr-item-wrapper {
  border-bottom: 1px solid #f1f5f9;
}

.attr-item-wrapper:last-child {
  border-bottom: none;
}

.attr-item {
  padding: 8px 6px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 11px;
  transition: background 0.1s;
}

.attr-item:hover {
  background: #e0e7ff;
}

.expand-icon {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #64748b;
  cursor: pointer;
  margin-top: 3px;
}

.expand-placeholder {
  width: 16px;
}

.attr-main {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
}

.attr-label {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.attr-path {
  font-size: 11px;
  color: #94a3b8;
  font-family: monospace;
  word-break: break-all;
  margin-top: 2px;
}

.attr-preview {
  font-size: 12px;
  color: #059669;
  font-weight: 500;
  flex-shrink: 0;
  max-width: 35%;
  text-align: right;
  word-break: break-all;
}

.val-empty {
  color: #9ca3af;
  font-style: italic;
  font-weight: normal;
}

.nested-tree-container {
  padding-left: 12px;
  background: #fff;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 12px;
}
</style>
